import { io, Socket } from 'socket.io-client';

class WebSocketService {
  private socket: Socket | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = Number(import.meta.env.VITE_APP_WEBSOCKET_RECONNECT_ATTEMPTS || 0);
  private joinedRooms = new Set<string>();
  private readonly reconnectDelay = Number(import.meta.env.VITE_APP_WEBSOCKET_RECONNECT_DELAY || 1000);
  private readonly reconnectDelayMax = Number(import.meta.env.VITE_APP_WEBSOCKET_RECONNECT_DELAY_MAX || 30000);
  private browserListenersBound = false;
  private healthCheckInterval: ReturnType<typeof setInterval> | null = null;
  /**
   * Controla se o socket já conectou ao menos uma vez nesta sessão.
   * Usado para distinguir conexão inicial (não dispara reconnect event)
   * de reconexões (dispara `app:socket-reconnect` para resync).
   */
  private hasConnectedOnce = false;
  /**
   * Quando true, todos os caminhos de auto-reconnect (visibility, focus,
   * online, health-check, disconnect-handler) ficam desabilitados. Usado
   * pelo botão "Testar desconexão" para isolar o teste manual.
   */
  private autoReconnectPaused = false;

  /** Pausa todos os mecanismos de reconexão automática. */
  pauseAutoReconnect() {
    this.autoReconnectPaused = true;
    console.warn('%c[WS] auto-reconnect PAUSADO (modo teste)', 'color: #c05621; font-weight: bold;');
  }

  /** Retoma a reconexão automática. */
  resumeAutoReconnect() {
    this.autoReconnectPaused = false;
    console.info('%c[WS] auto-reconnect RETOMADO', 'color: #2f855a;');
  }

  isAutoReconnectPaused(): boolean {
    return this.autoReconnectPaused;
  }

  private getStoredToken(): string | null {
    const rawToken = localStorage.getItem('@revalida.token');

    if (!rawToken) {
      return null;
    }

    try {
      return JSON.parse(rawToken);
    } catch {
      return rawToken;
    }
  }

  connect() {
    // Já conectado: nada a fazer.
    if (this.socket?.connected) {
      return;
    }

    const token = this.getStoredToken();
    if (!token) {
      console.error('Token não encontrado');
      return;
    }

    // CRÍTICO: se já existe uma instância de socket, reusar. Criar uma
    // instância nova aqui faz com que os listeners (`socket.on(...)`)
    // registrados por outros componentes fiquem órfãos na instância
    // antiga — eventos do servidor chegam mas ninguém está ouvindo.
    // Reutilizar mantém todos os listeners válidos entre ciclos de
    // disconnect/reconnect.
    if (this.socket) {
      this.socket.auth = { token };
      if (!this.socket.connected) {
        this.socket.connect();
      }
      return;
    }

    const websocketUrl = import.meta.env.VITE_APP_WEBSOCKET_URL || import.meta.env.VITE_APP_URL || 'http://localhost:3333';

    const allowPolling = String(import.meta.env.VITE_APP_WEBSOCKET_ALLOW_POLLING || '').toLowerCase() === 'true';
    const transports: any[] = allowPolling ? ['websocket', 'polling'] : ['websocket'];

    this.socket = io(websocketUrl, {
      auth: { token },
      transports,
      reconnection: true,
      reconnectionDelay: this.reconnectDelay,
      reconnectionDelayMax: this.reconnectDelayMax,
      timeout: 20000,
      ...(this.maxReconnectAttempts > 0
        ? { reconnectionAttempts: this.maxReconnectAttempts }
        : {})
    });

    this.setupEventListeners();
    this.bindBrowserListeners();
    this.startHealthCheck();
  }

  private setupEventListeners() {
    if (!this.socket) return;

    this.socket.on('connect', () => {

      this.reconnectAttempts = 0;
      const isReconnect = this.hasConnectedOnce;
      this.hasConnectedOnce = true;

      console.info('%cWebSocket: conectado', 'color: green;', {
        id: this.socket?.id,
        transport: this.socket?.io.engine.transport.name,
        isReconnect,
      });

      // Re-entrar nas salas após conectar/reconectar
      this.rejoinRooms();

      // Se foi uma reconexão (não a conexão inicial), disparar o mesmo
      // evento que o botão manual dispara — para que a UI faça o resync
      // completo (refetchRoom, recuperar estação finalizada, etc.).
      // Na primeira conexão não disparamos porque a UI já está inicializando
      // com dados frescos.
      if (isReconnect) {
        // Pequeno atraso para dar tempo do servidor processar join-room
        // e o broadcast entered-or-left-room chegar antes do refetch.
        setTimeout(() => {
          window.dispatchEvent(
            new CustomEvent('app:socket-reconnect', {
              detail: {
                rooms: Array.from(this.joinedRooms),
                source: 'auto',
              },
            })
          );
        }, 200);
      }
    });

    this.socket.on('disconnect', (reason) => {
      console.warn('%cWebSocket: desconectado', 'color: orange;', {
        reason,
        id: this.socket?.id
      });

      // Quando o servidor força disconnect (ex.: kill do PM2 restart),
      // o socket.io-client NÃO tenta reconectar sozinho nesses casos.
      // Forçar reconexão manual — a menos que auto-reconnect esteja pausado.
      if (
        !this.autoReconnectPaused &&
        (reason === 'io server disconnect' || reason === 'transport close')
      ) {
        setTimeout(() => {
          if (this.autoReconnectPaused) return;
          if (this.socket && !this.socket.connected) {
            const token = this.getStoredToken();
            if (token) {
              this.socket.auth = { token };
            }
            this.socket.connect();
          }
        }, 500);
      }
    });

    this.socket.on('connect_error', (error) => {
      console.error('%cWebSocket: Erro de conexão!', 'color: red; font-weight: bold;', {
        message: error.message,
        type: error.name,
        context: error
      });

      this.reconnectAttempts++;

      if (
        this.maxReconnectAttempts > 0 &&
        this.reconnectAttempts >= this.maxReconnectAttempts
      ) {
        console.error('Máximo de tentativas de reconexão atingido');
      }
    });

    this.socket.io.on('reconnect_attempt', (attempt) => {
      const token = this.getStoredToken();
      this.reconnectAttempts = attempt;

      if (token && this.socket) {
        this.socket.auth = { token };
      }

      console.info('%cWebSocket: tentando reconectar', 'color: #b7791f;', {
        attempt,
        rooms: Array.from(this.joinedRooms)
      });
    });

    this.socket.io.on('reconnect', () => {
      // Garantir rejoin mesmo se o evento 'connect' for disparado antes
      // de os listeners estarem reassinados nos componentes.
      this.rejoinRooms();
    });

    this.socket.io.on('reconnect_failed', () => {
      console.error('WebSocket: reconexão falhou definitivamente');
    });

    this.socket.on('error', (error: any) => {
      console.error('WebSocket Error:', error);

      // Se token expirado/inválido, tentar um refresh transparente antes
      // de deslogar. O interceptor do axios já renova o token; aqui
      // apenas lemos o novo token do storage e reconectamos.
      if (error?.code === 'TOKEN_EXPIRED' || error?.code === 'INVALID_TOKEN') {
        const latestToken = this.getStoredToken();
        if (latestToken && this.socket && latestToken !== (this.socket.auth as any)?.token) {
          this.socket.auth = { token: latestToken };
          try { this.socket.disconnect(); } catch { }
          this.socket.connect();
          return;
        }

        // Sem token válido: limpar sessão e redirecionar
        localStorage.removeItem('@revalida.token');
        localStorage.removeItem('@revalida.user');
        window.location.href = '/login';
      }
    });
  }

  private rejoinRooms() {
    console.info('%c[WS] rejoinRooms called', 'color: #2b6cb0; font-weight: bold;', {
      connected: this.socket?.connected,
      socketId: this.socket?.id,
      joinedRooms: Array.from(this.joinedRooms),
    });
    if (!this.socket?.connected) {
      console.warn('[WS] rejoinRooms abortado: socket não conectado');
      return;
    }
    if (this.joinedRooms.size === 0) {
      console.warn('[WS] rejoinRooms abortado: joinedRooms vazio');
      return;
    }

    for (const roomId of this.joinedRooms) {
      console.info('[WS] emit join-room', { roomId });
      this.socket.emit('join-room', { roomId });
    }
  }

  /**
   * Força reemissão de join-room para todas as salas conhecidas.
   * Útil após uma reconexão manual para garantir que o servidor
   * reenvie a lista de presença atual (entered-or-left-room).
   */
  rejoinAllRooms() {
    this.rejoinRooms();
  }

  getJoinedRooms(): string[] {
    return Array.from(this.joinedRooms);
  }

  /**
   * Listeners de navegador para reconexão proativa:
   *  - visibilitychange: aba voltou ao foco → se desconectado, reconectar
   *  - focus: janela em foco → mesma ideia
   *  - online: rede voltou → reconectar
   *  - pagehide: aviso ao servidor que estamos saindo (best-effort)
   */
  private bindBrowserListeners() {
    if (this.browserListenersBound) return;
    if (typeof window === 'undefined' || typeof document === 'undefined') return;

    const ensureConnected = (reason: string) => {
      if (this.autoReconnectPaused) {
        console.info('[WS] auto-reconnect pausado, ignorando trigger:', reason);
        return;
      }
      if (!this.socket) return;
      if (this.socket.connected) return;
      console.info('%cWebSocket: reconectando (%s)', 'color: #4a5568;', reason);
      const token = this.getStoredToken();
      if (token) {
        this.socket.auth = { token };
      }
      this.socket.connect();
    };

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        ensureConnected('visibilitychange');
      }
    });

    window.addEventListener('focus', () => ensureConnected('focus'));
    window.addEventListener('online', () => ensureConnected('online'));

    window.addEventListener('pagehide', () => {
      if (!this.socket?.connected) return;
      // best-effort: deixa o servidor saber que a aba está fechando
      for (const roomId of this.joinedRooms) {
        try { this.socket.emit('leave-room', { roomId }); } catch { }
      }
    });

    this.browserListenersBound = true;
  }

  /**
   * Health check suave: a cada 30s, se o navegador diz estar online e o
   * socket está desconectado, força uma reconexão. Complementa o backoff
   * nativo do socket.io-client para casos em que ele para de tentar
   * (ex.: quando o servidor devolveu erro fatal).
   */
  private startHealthCheck() {
    if (this.healthCheckInterval) return;

    this.healthCheckInterval = setInterval(() => {
      if (this.autoReconnectPaused) return;
      if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
      if (!this.socket) return;
      if (this.socket.connected) return;

      const token = this.getStoredToken();
      if (!token) return;

      console.info('%cWebSocket: health-check → reconectando', 'color: #4a5568;');
      this.socket.auth = { token };
      this.socket.connect();
    }, 30000);
  }

  /**
   * Encerra o socket completamente (usado no logout).
   * ATENÇÃO: não chamar para quedas transientes — a reconexão deve
   * usar `socket.connect()` diretamente ou o auto-reconnect do service.
   * Chamar disconnect() destrói a instância e invalida todos os
   * listeners que outros componentes já registraram.
   */
  disconnect() {
    console.warn('%c[WS] disconnect() chamado — limpando socket e joinedRooms', 'color: red; font-weight: bold;', {
      joinedRooms: Array.from(this.joinedRooms),
      stack: new Error().stack?.split('\n').slice(1, 8).join('\n'),
    });
    if (this.healthCheckInterval) {
      clearInterval(this.healthCheckInterval);
      this.healthCheckInterval = null;
    }
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
    this.joinedRooms.clear();
  }

  getSocket(): Socket | null {
    return this.socket;
  }

  isConnected(): boolean {
    return this.socket?.connected || false;
  }

  // Métodos auxiliares
  emit(event: string, data: any) {
    if (this.socket) {
      this.socket.emit(event, data);
    } else {
      console.warn('Socket não conectado. Não foi possível emitir evento:', event);
    }
  }

  request<T = any>(event: string, data: any, timeoutMs = 5000): Promise<T> {
    return new Promise((resolve, reject) => {
      if (!this.socket) {
        reject(new Error(`Socket não conectado (${event})`));
        return;
      }

      if (!this.socket.connected) {
        reject(new Error(`Socket desconectado (${event})`));
        return;
      }

      this.socket.timeout(timeoutMs).emit(event, data, (err: any, response: T) => {
        if (err) {
          reject(err);
          return;
        }
        resolve(response);
      });
    });
  }

  on(event: string, callback: (...args: any[]) => void) {
    if (this.socket) {
      this.socket.on(event, callback);
    }
  }

  off(event: string, callback?: (...args: any[]) => void) {
    if (this.socket) {
      this.socket.off(event, callback);
    }
  }

  joinRoom(roomId: string) {
    console.info('%c[WS] joinRoom', 'color: #2b6cb0;', { roomId, current: Array.from(this.joinedRooms) });
    this.joinedRooms.add(roomId);
    this.emit('join-room', { roomId });
  }

  leaveRoom(roomId: string) {
    console.warn('%c[WS] leaveRoom', 'color: #c05621;', {
      roomId,
      current: Array.from(this.joinedRooms),
      stack: new Error().stack?.split('\n').slice(1, 6).join('\n'),
    });
    this.joinedRooms.delete(roomId);
    this.emit('leave-room', { roomId });
  }

  // Métodos para eventos das salas
  startStation(roomId: string, stationId: string) {
    this.emit('start-station', { roomId, stationId });
  }

  endStation(roomId: string, stationId: string) {
    this.emit('end-station', { roomId, stationId });
  }

  closeStation(roomId: string, stationId: string) {
    this.emit('close-station', { roomId, stationId });
  }

  releaseButton(roomId: string, button: string, id?: string | number) {
    this.emit('release-button', { roomId, button, id });
  }

  scriptPatientRead(roomId: string, status: boolean) {
    this.emit('script-patient-read', { roomId, status });
  }

  stopwatchControl(roomId: string, status: 'iniciado' | 'pausado' | 'stop') {
    this.emit('stopwatch-control', { roomId, status });
  }

  closeRoom(roomId: string) {
    this.emit('close-room', { roomId });
  }

  changeController(roomId: string, controllerId: string) {
    this.emit('change-controller', { roomId, controllerId });
  }

  dispatchApito(roomId: string, status: 'dispara' | 'active' | 'inactive', isDispatch: boolean) {
    this.emit('dispatch-apito', { roomId, status, isDispatch });
  }

  updateEvaluationForm(roomId: string, status: 'Finalizado' | 'Iniciado', stationId?: string) {
    this.emit('update-evaluation-form', { roomId, status, stationId });
  }
}

export const websocketService = new WebSocketService();
export const socket = websocketService.getSocket();
